import { useEffect, useState, memo, useMemo } from 'react';
import { useRouter } from 'next/router';
import { Box, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import InView from 'react-intersection-observer';
import { useMutation, useReactiveVar } from '@apollo/client';
import { CREATE_SUBSCRIPTION_CHECKOUT } from '@graphql/mutations/orderFlow';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';

import theme from 'src/style-system/theme';
import {
  filtersStateVar,
  updateFilterSelection,
  usePersistentFilterSelection,
} from 'store/filter';
import { usePersistentCartItems } from 'store/cartItems';
import {
  mealCountWithoutSides,
  productFlatListVar,
} from 'store/productsFlatList';
import {
  setDirectRoutingValidation,
  snackbarInfo,
  subscriptionIdStateVar,
  utmQueryVar,
} from 'store/utils';

import OrderFlowFooter from '@components/OrderFlowFooter';
import { AUTO_PROMO_CODE, orderFlowfooterContent } from '@utils/data';
import {
  allFiltersType,
  getCategorizedProductsType,
  productType,
  productIdsQuantityType,
  TfrequenciesV2,
} from '@utils/types';
import { AppRoutes } from '@utils/appRoutes';
import CuminEightCrossIcon from '@public/assets/svg/CuminEightCrossIcon.svg';
import BackToTop from '@public/assets/svg/BackToTop.svg';
import { usePersistentplanAndFreq } from 'store/planAndFreq';
import TabPanelContent from '@components/TabPanelContent';
import DesktopFilter from '@containers/OrderStepTwo/PanelTwoComponent/DesktopFilter';
import Bundles from '@containers/OrderStepTwo/Bundles';
import BaseDrawer from '@containers/OrderStepTwo/PanelTwoComponent/BaseDrawer';
import FilterDrawer from '@containers/OrderStepTwo/PanelTwoComponent/FilterDrawer';
import MealDrawer from '@containers/OrderStepTwo/PanelTwoComponent/MealDrawer';
import { calculateMealCounts } from '@utils/helpers';

import { useStyles } from './useStyles';
import useAuth from '@utils/useAuth';
import { usePersistentPromoCode } from 'store/promoCode';
import { useElevar } from '@utils/elevar';

interface IProductCategories {
  categories: getCategorizedProductsType[];
  filterData: allFiltersType[];
  countryCode: string;
  frequenciesV2: any;
  setLoading: (loading: boolean) => void;
}

const ProductCategories = ({
  categories,
  filterData,
  countryCode,
  frequenciesV2,
  setLoading,
}: IProductCategories) => {
  const BUNDLE_ID = 20;

  const [isFilterDrawerOpen, setfilterDrawerOpen] = useState<boolean>(false);
  const [headerIndex, setHeaderIndex] = useState<number>(BUNDLE_ID);
  const [categoriesData, setCategoriesData] =
    useState<getCategorizedProductsType[]>(categories);

  const {
    elevarAddToCartEvent,
    elevarRemoveFromCartEvent,
    elevarViewCartEvent,
    elevarViewItemEvent,
  } = useElevar();

  const mealCounts = useReactiveVar(mealCountWithoutSides);
  const initialQuery = useReactiveVar(utmQueryVar);

  const router = useRouter();
  // product_id for to open open product modal
  const {
    TRAIL_PLAN,
    withUpgrade,
    product_id,
    featured_category_id,
    hide_category_ids, //1,2,3
  } = router?.query;

  const { authState } = useAuth();
  const promoCodeValue = usePersistentPromoCode();
  const { productFlatList } = useReactiveVar(productFlatListVar);
  const cartItems = usePersistentCartItems();
  const filterSelect = usePersistentFilterSelection();
  const planAndFreq = usePersistentplanAndFreq();

  const [createSubscriptionCheckout, { loading, error, data }] = useMutation(
    CREATE_SUBSCRIPTION_CHECKOUT
  );

  const subscriptionId = useReactiveVar(subscriptionIdStateVar);

  const classes = useStyles();

  // for featured_category_id, hide_category_ids support
  useEffect(() => {
    if (categories) {
      let modifiedCategories = categories;
      if (featured_category_id) {
        let indexOfCategoryToShowFirst;

        const firstCategoryToShow = categories?.find((category, index) => {
          if (category.category_id === +featured_category_id) {
            indexOfCategoryToShowFirst = index;
            return true;
          }
          return false;
        });

        if (firstCategoryToShow && indexOfCategoryToShowFirst) {
          modifiedCategories.splice(indexOfCategoryToShowFirst, 1);
          modifiedCategories.unshift(firstCategoryToShow);
        }
      }

      //for hide category
      if (hide_category_ids) {
        let hide_category_ids_Arr: string[] = [];

        //@ts-ignore
        hide_category_ids_Arr = hide_category_ids.split(',');
        modifiedCategories = categories?.filter((category) => {
          if (hide_category_ids_Arr.includes(category.category_id.toString())) {
            return false;
          }
          return true;
        });
      }
      if (featured_category_id || hide_category_ids) {
        setCategoriesData(modifiedCategories);
      }
    }
  }, [featured_category_id, hide_category_ids, categories]);

  // create product list
  useEffect(() => {
    let productFlatList: { [product_id: number]: productType } = {};
    categories?.map((category) => {
      if (category.uiType !== 'BUNDLE') {
        category.products.map((product) => {
          if (!productFlatList[product.id]) {
            productFlatList[product.id] = {
              ...product,
              category_name: category.category_name,
            };
          }
        });
      }
    });
    productFlatListVar({
      productFlatList: productFlatList,
    });
  }, [categories]);

  //remove filter for every refresh
  useEffect(() => {
    filtersStateVar({});
    const tccSubsId = localStorage.getItem('tcc_subs_id'); // get subcription id from local storage
    if (tccSubsId) {
      subscriptionIdStateVar(JSON.parse(tccSubsId));
    }
  }, []);

  useEffect(() => {
    if (isFilterDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isFilterDrawerOpen]);

  // for calculate meal count for all cases
  useEffect(() => {
    if (cartItems) {
      const mealCount = calculateMealCounts({
        productFlatList,
        cartItems,
        categories,
      });

      if (mealCount >= 0) {
        mealCountWithoutSides(mealCount);
      }
    }
  }, [cartItems, productFlatList]);

  useEffect(() => {
    if (categories) {
      const filteredCategories = categories
        ?.filter(
          ({ category_name, uiType }) =>
            category_name !== 'sides' && uiType !== 'BUNDLE'
        )
        ?.sort((a, b) => {
          if (a.category_name === 'Breakfast') {
            return -1;
          }
          return 0;
        });
      if (filteredCategories?.length) {
        setHeaderIndex(filteredCategories[0].category_id);
      }
    }
  }, [categories]);

  //for open product modal on geeting productid from query params
  useEffect(() => {
    if (product_id) {
      const product = document.getElementById(product_id as string);
      product?.click();
    }
  }, [product_id]);

  const handleDrawerOpen = () => {
    setfilterDrawerOpen(true);
  };

  const onClickHeaderTitle = (categoryId: number) => {
    // for sepecific section to scroll into view
    const element = document.getElementById(categoryId.toString());
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    if (element) {
      element.style.scrollMarginTop = '250px';
    }

    setHeaderIndex(categoryId);
  };

  const onClearFilter = (
    filterId: number,
    filterGroupName: string,
    filterLabel: string
  ) => {
    updateFilterSelection(filterId, false, filterGroupName, filterLabel);
  };

  const createSubscription = async ({
    country_code,
    productIdsQuantity,
  }: any) => {
    try {
      setLoading(true);
      const sellingPlanId =
        frequenciesV2?.find(
          (item: any) => item.value === planAndFreq?.selectedFrequency
        )?.id || 0;

      const discountCode =
        promoCodeValue || (!authState?.user?.email ? AUTO_PROMO_CODE : '');

      const subscriptionData: any = await createSubscriptionCheckout({
        variables: {
          input: {
            countryCode: country_code,
            id: subscriptionId || '',
            planId: planAndFreq?.selectedPlanId,
            productIdsQuantity,
            sellingPlanId,
            attributes: {
              discount: discountCode,
              query: initialQuery,
            },
          },
        },
      });

      const { id, url } =
        subscriptionData?.data?.createSubscriptionCheckout || {};
      if (id && !subscriptionId) {
        subscriptionIdStateVar(id);
      }

      if (id && url) {
        router.push(url);
        localStorage.setItem('tcc_subs_id', JSON.stringify(id)); // save subcription id to local storage
      }
    } catch (error: any) {
      snackbarInfo({ message: error.message });
    } finally {
      // setLoading(false);
    }
  };

  const onCheckout = async () => {
    document.body.style.overflow = 'unset';
    const getSelectedCountryCode = localStorage.getItem('iso_alpha_3_code');

    if (countryCode || getSelectedCountryCode) {
      const country_code =
        countryCode ||
        (getSelectedCountryCode ? JSON.parse(getSelectedCountryCode) : '');

      const productIdsQuantity: productIdsQuantityType[] = [];
      const cartItemsKeyArr = Object.keys(cartItems);
      cartItemsKeyArr?.forEach((key) => {
        if (cartItems[key] > 0) {
          const obj = {
            product_id: +key,
            quantity: cartItems[key],
          };
          productIdsQuantity.push(obj);
        }
      });

      if (productIdsQuantity?.length) {
        createSubscription({ country_code, productIdsQuantity });
      }

      setDirectRoutingValidation(true);
      //   window.scroll(0, 0);
      //   if (TRAIL_PLAN) {
      //     router.push(
      //       {
      //         pathname: `/${country_code}${AppRoutes.ORDER_STEP_THREE}`,
      //         query: { TRAIL_PLAN: 'TRAIL_PLAN', withUpgrade },
      //       },
      //       `/${country_code}${AppRoutes.ORDER_STEP_THREE}`,
      //       { shallow: true }
      //     );
      //   } else {
      //     router.push(`/${country_code}${AppRoutes.ORDER_STEP_THREE}`);
      //   }
      // }
    }
  };

  // for cta text of checkout button on desktop meal drawer as well as mobile base drawer
  const getCtaText = useMemo(() => {
    if (loading) {
      return 'Loading...';
    } else if (!(mealCounts >= planAndFreq.selectedPlan)) {
      // for subscription meal change, current order change and order flow
      const numberOfMealLeftToAdd = planAndFreq.selectedPlan - mealCounts;
      return `Add ${numberOfMealLeftToAdd} More Items`;
    } else {
      return 'Checkout';
    }
  }, [mealCounts, planAndFreq.selectedPlan, loading]);

  // get disabled of checkout button on desktop meal drawer as well as mobile base drawer
  const isDisabled: boolean = useMemo(() => {
    if (
      // for order flow
      !(mealCounts >= planAndFreq.selectedPlan)
    ) {
      return true;
    }
    return false;
  }, [planAndFreq.selectedPlan, mealCounts]);

  const onVisible = (inView: boolean, index: number) => {
    if (inView) {
      setHeaderIndex(index);
    }
  };

  const handleBackToTop = () => {
    document?.body.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <>
      <Box bgcolor="whitePepper.main" className={classes.mainPageContainer}>
        <Box className={classes.filterContainer}>
          <DesktopFilter
            filterData={filterData}
            isBgColor={true}
            isSubscriptionPage={false}
            isNoMarginAtTop={true}
          />
        </Box>
        <Box className={classes.container}>
          <Box
            className={classes.headerContainer}
            sx={{ top: TRAIL_PLAN ? '71px' : '78px' }}
          >
            <Box
              onClick={handleDrawerOpen}
              sx={{
                [theme.breakpoints.up('md')]: {
                  display: 'none',
                },
              }}
            >
              <FilterListIcon
                fontSize="large"
                sx={{ color: 'cinnamon.main', cursor: 'pointer' }}
              />
            </Box>
            <Box className={classes.headerTitleWrapper}>
              {categoriesData
                ?.filter(
                  ({ category_name, uiType }) =>
                    category_name !== 'sides' && uiType !== 'BUNDLE'
                )
                ?.sort((a, b) => {
                  if (a.category_name === 'Breakfast') {
                    return -1;
                  }
                  return 0;
                })
                ?.map((item) => (
                  <Typography
                    key={item.category_id}
                    variant="body2"
                    color="garamMasala.main"
                    id={item.category_name}
                    onClick={() => onClickHeaderTitle(item.category_id)}
                    sx={{
                      textDecoration:
                        headerIndex === item.category_id
                          ? `underline solid ${theme.palette.turmeric.main} 10px`
                          : '',
                      textUnderlineOffset: '15px',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    {item.category_name[0].toUpperCase() +
                      item.category_name.slice(1)}
                  </Typography>
                ))}
              {hide_category_ids ? null : (
                <Typography
                  variant="body2"
                  color="garamMasala.main"
                  onClick={() => onClickHeaderTitle(BUNDLE_ID)}
                  sx={{
                    textDecoration:
                      headerIndex === BUNDLE_ID
                        ? `underline solid ${theme.palette.turmeric.main} 10px`
                        : '',
                    textUnderlineOffset: '15px',
                    cursor: 'pointer',
                  }}
                >
                  Bundles
                </Typography>
              )}
            </Box>
          </Box>

          {/* Filter selected list in mobile view */}

          <Box
            className={classes.filterListWrapper}
            sx={{
              paddingTop: Object.keys(filterSelect)?.length ? '20px' : '0px',
            }}
          >
            {filterData?.map((item) =>
              item.filters.map((filter) =>
                filterSelect[filter.id]?.isSelected ? (
                  <Box className={classes.filterList} key={filter.id}>
                    <Typography variant="caption" color="cumin.main">
                      {filter.label}
                    </Typography>
                    <CuminEightCrossIcon
                      onClick={() =>
                        onClearFilter(
                          filter.id,
                          item.filter_group_name,
                          filter.label
                        )
                      }
                    />
                  </Box>
                ) : null
              )
            )}
          </Box>

          <Box className={classes.panelcontentWrapper}>
            {categoriesData
              ?.filter(
                ({ category_name, uiType }) =>
                  category_name !== 'sides' && uiType !== 'BUNDLE'
              )
              ?.sort((a, b) => {
                if (a.category_name === 'Breakfast') {
                  return -1;
                }
                return 0;
              })
              ?.map((item, index) => (
                <InView
                  key={item.category_id}
                  threshold={0.4}
                  onChange={(inView: boolean) =>
                    onVisible(inView, item.category_id)
                  }
                >
                  <Box id={item.category_id.toString()}>
                    <TabPanelContent
                      products={item.products}
                      categoryName={item.category_name}
                      categoryDescription={item.categoryDescription}
                      elevarAddToCartEvent={elevarAddToCartEvent}
                      elevarRemoveFromCartEvent={elevarRemoveFromCartEvent}
                      elevarViewItemEvent={elevarViewItemEvent}
                    />
                  </Box>
                  {!index && featured_category_id ? (
                    <>
                      <Typography
                        variant="h2"
                        align="center"
                        color="cinnamon.main"
                        mt="64px"
                        mb="8px"
                      >
                        Complete Meals
                      </Typography>

                      <Typography
                        variant="body2"
                        align="center"
                        color="cumin.main"
                        mb="10px"
                      >
                        You can also check out our popular just-add-water 5
                        minutes meals (protein included)
                      </Typography>
                    </>
                  ) : null}
                </InView>
              ))}
            {hide_category_ids ? null : (
              <InView
                threshold={0.2}
                onChange={(inView: boolean) => onVisible(inView, BUNDLE_ID)}
              >
                <Bundles
                  categories={categories}
                  bundleId={BUNDLE_ID}
                  elevarAddToCartEvent={elevarAddToCartEvent}
                />
              </InView>
            )}
          </Box>
          <Box
            className={classes.backToTopBtnWrapper}
            onClick={handleBackToTop}
          >
            <BackToTop />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          [theme.breakpoints.down('md')]: {
            display: 'none',
          },
        }}
      >
        <OrderFlowFooter footerContent={orderFlowfooterContent} />
      </Box>

      <BaseDrawer
        categories={categories}
        mealCounts={mealCounts}
        onCheckout={onCheckout}
        getCtaText={getCtaText}
        isDisabled={isDisabled}
        countryCode={countryCode}
        elevarAddToCartEvent={elevarAddToCartEvent}
        elevarRemoveFromCartEvent={elevarRemoveFromCartEvent}
        elevarViewCartEvent={elevarViewCartEvent}
      />
      <Box
        sx={{
          [theme.breakpoints.down('md')]: {
            display: 'none',
          },
        }}
      >
        <MealDrawer
          categories={categories}
          mealCounts={mealCounts}
          onCheckout={onCheckout}
          getCtaText={getCtaText}
          isDisabled={isDisabled}
          countryCode={countryCode}
          elevarAddToCartEvent={elevarAddToCartEvent}
          elevarRemoveFromCartEvent={elevarRemoveFromCartEvent}
          elevarViewCartEvent={elevarViewCartEvent}
        />
      </Box>
      <FilterDrawer
        filterData={filterData}
        setOpen={setfilterDrawerOpen}
        open={isFilterDrawerOpen}
      />
    </>
  );
};

export default memo(ProductCategories);
